import { MonolithCartCostsContainer } from '@app/components/Costs';
import { MonolithApplyCouponContainer } from '@app/components/Coupons';
import { CartLineItemListContainer } from '@app/components/LineItemsList';
import { mapCartMessageFromMonolith } from '@app/components/Messages/utils';
import { MonolithCartPaypalExpressContainer } from '@app/components/PayPalExpress/MonolithCartPaypalExpressContainer';
import { MonolithCartStripeContainerLazy } from '@app/components/Stripe/MonolithCartStripeContainerLazy';
import { PageSource } from '@app/components/Stripe/types';
import {
  useCartAndDeliveryDatesDataContext,
  useFetchCartAndDeliveryDates,
  useShippingMethod,
} from '@app/data/clients/cartCheckoutClient';
import { useTrackGA4ViewCart } from '@app/data/clients/cartCheckoutClient/CartAndDeliveryDatesDataContext/hooks/useTrackGA4ViewCart';
import { Box, Divider, PageSection, Text } from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { Seo } from '@redbubble/next/seo';
import { DeviceType, UserInfo } from '@redbubble/next/types';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import { AnalyticsPageType } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import { RSCheckoutLocation } from '@redbubble/next-provider/rudderstack';
import type { NextPage } from 'next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorCart } from '../../components/ErrorCart';
import { GroupedMessages, Messages } from '../../components/Messages';
import { Message, MessageSeverity } from '../../components/Messages/types';
import {
  isFlatRateShippingApplied,
  isNotShippingMessage,
} from '../../components/Messages/utils';
import { ErrorMessage, usePaymentRedirectError } from '../../components/Stripe';
import { UserAgreement } from '../../components/UserAgreement';
import { EmptyCart } from './components/EmptyCart';
import { OrderSummary } from './components/OrderSummary';
import { MonolithCartPageHeadingContainer } from './components/PageHeading';
import { TrustSignalsGroup } from './components/TrustSignalsGroup';
import styles from './Index.module.scss';
import { cartMessages } from './messages';

export const intlMessages = defineMessages({
  pageTitle: {
    defaultMessage: 'Cart',
  },
});

const CartPage: NextPage = () => {
  const { client, dispatch, cart, cartLoading, errors } =
    useCartAndDeliveryDatesDataContext();
  useFetchCartAndDeliveryDates(client, dispatch);

  const [{ locale }] = useUserInfo<UserInfo>();
  const { isOrAbove } = useViewportWidth();
  const intl = useIntl();
  const { error: paymentRedirectError } = usePaymentRedirectError();

  useTrackGA4ViewCart();

  const isErrorCart = (errors ?? []).length > 0 && !cart;

  // TODO [BX-735]: Consider using global state
  const [shippingPreferenceLoading, setShippingPreferenceLoading] =
    useState(false);

  // Server error
  const [lineItemServerErrorMessages, setLineItemServerErrorMessages] =
    useState<Message[]>([]);

  const [serverErrorMessage, setServerErrorMessage] = useState<
    Message | undefined
  >(undefined);

  const [expressPayErrorMessage, setExpressPayErrorMessage] = useState<
    Message | undefined
  >(undefined);
  const cartShippingRemovalVariant = useAmplitudeExperiment(
    ExperimentConfig.CartShippingRemoval
  );
  const isCartShippingRemovalTreatment =
    cartShippingRemovalVariant === 'treatment';
  const displayShippingOptions = !isCartShippingRemovalTreatment;
  const showInclVatText =
    isCartShippingRemovalTreatment && !cart?.summary.show_tax_separately;
  const messages = useMemo(
    () => (cart?.messages || []).map(mapCartMessageFromMonolith),
    [cart]
  );
  const flatRateApplied = useMemo(
    () => messages.some(isFlatRateShippingApplied),
    [messages]
  );
  const digitalProductsOnly = cart?.digital_products_only || false;
  const isEmptyCart = cart?.total_items_count === 0 && !cartLoading;
  const selectedShipping = useShippingMethod();

  // Redirect to the Explore page whenever the cart is empty
  useEffect(() => {
    if (isEmptyCart) {
      window.location.assign('/explore/');
    }
  }, [isEmptyCart]);

  const handleLineItemServerError = (errors: Message[]) => {
    const error = errors.find(({ itemId }) => !!itemId);
    if (!error?.itemId) return;

    const filteredMessages = lineItemServerErrorMessages.filter(
      (currentErrorMessage) => currentErrorMessage.itemId !== error.itemId
    );

    if (error.text) {
      filteredMessages.push(error);
    }

    setLineItemServerErrorMessages(filteredMessages);
  };

  const handleServerError = (msg: string) => {
    if (msg) {
      setServerErrorMessage({
        text: msg,
        severity: MessageSeverity.Error,
        applied: false,
      });
    }
  };

  const handleExpressPayError = useCallback(
    (errorMessage: ErrorMessage) => {
      if (errorMessage) {
        setExpressPayErrorMessage({
          severity: MessageSeverity.Error,
          text:
            typeof errorMessage === 'string'
              ? errorMessage
              : intl.formatMessage(errorMessage),
          applied: false,
        });
      } else {
        setExpressPayErrorMessage(undefined);
      }
    },
    [intl]
  );

  useEffect(() => {
    if (paymentRedirectError) handleExpressPayError(paymentRedirectError);
  }, [paymentRedirectError, handleExpressPayError]);

  const clearExpressPaymentsError = useCallback(
    () => handleExpressPayError(''),
    [handleExpressPayError]
  );

  const clearServerError = () => {
    setServerErrorMessage(undefined);
  };

  const renderCartContent = ({
    errorCart,
    emptyCart,
    normalCart,
  }: {
    errorCart: React.ReactNode;
    emptyCart: React.ReactNode;
    normalCart: React.ReactNode;
  }) => {
    if (isErrorCart) {
      return errorCart;
    }

    if (isEmptyCart) {
      return emptyCart;
    }

    return normalCart;
  };
  const allMessages = (() => {
    const msgs: Message[] = [];

    if (serverErrorMessage) {
      msgs.push(serverErrorMessage);
    }

    if (expressPayErrorMessage && isOrAbove(DeviceType.Desktop)) {
      msgs.push(expressPayErrorMessage);
    }

    msgs.push(...messages, ...lineItemServerErrorMessages);

    return msgs;
  })();

  const nonShippingMessages = useMemo(
    () => allMessages.filter(isNotShippingMessage),
    [allMessages]
  );

  const isOrderSummaryLoading = shippingPreferenceLoading;

  const showExpressPayErrorInOrderSummary =
    expressPayErrorMessage && !isOrAbove(DeviceType.Desktop);

  return (
    <PageSection paddingX="m" paddingY="xl" size="small" element="main">
      <Seo title={intl.formatMessage(intlMessages.pageTitle)} noindex />
      <Box className={styles.wrapper} data-empty-cart={isEmptyCart}>
        <Box className={styles.heading}>
          <MonolithCartPageHeadingContainer />
          {/* TODO[BX-638]: will look into how we want to handle line item errors */}
          <Box marginTop="l">
            <GroupedMessages messages={nonShippingMessages} />
          </Box>

          <Divider marginTop="l" marginBottom="xs" />
        </Box>

        {renderCartContent({
          emptyCart: <EmptyCart />,
          errorCart: <ErrorCart />,
          normalCart: (
            <>
              <Box className={styles.lineItems}>
                <CartLineItemListContainer
                  editable
                  analyticsPageType="cart"
                  setLineItemUpdateErrors={handleLineItemServerError}
                  showInclVatText={showInclVatText}
                />
              </Box>
              <Box className={styles.summary}>
                <Box className={styles.orderSummary}>
                  <OrderSummary loading={isOrderSummaryLoading}>
                    {!digitalProductsOnly && displayShippingOptions && (
                      <OrderSummary.Shipping
                        handleServerError={handleServerError}
                        clearServerError={clearServerError}
                        handleShippingPreferenceLoading={
                          setShippingPreferenceLoading
                        }
                        allMessages={allMessages}
                        flatRateApplied={flatRateApplied}
                      />
                    )}

                    <Text type="display5" marginBottom="m" display="block">
                      {intl.formatMessage(cartMessages.orderSummaryText)}
                    </Text>

                    <MonolithCartCostsContainer
                      includeShippingTaxWithShippingAmount
                      pendingTaxText={intl.formatMessage(
                        cartMessages.calculateTaxAtCheckout
                      )}
                      showSubtotalOnly={isCartShippingRemovalTreatment}
                    />
                    {showExpressPayErrorInOrderSummary && (
                      <Messages messages={[expressPayErrorMessage]} />
                    )}
                    {digitalProductsOnly ? (
                      <OrderSummary.DigitalShipping />
                    ) : (
                      <Box marginBottom="m">
                        <OrderSummary.CheckoutButton
                          shippingMethod={selectedShipping}
                        />
                      </Box>
                    )}
                    <Box className={styles.expressPaymentMethod}>
                      <MonolithCartStripeContainerLazy
                        analyticsPageType={AnalyticsPageType.Cart}
                        pageSource={PageSource.cart}
                        onPayError={handleExpressPayError}
                        onCancel={clearExpressPaymentsError}
                        onConfirm={clearExpressPaymentsError}
                        onLaunch={clearExpressPaymentsError}
                        rsCheckoutLocation={RSCheckoutLocation.Cart}
                      />
                    </Box>

                    <Box className={styles.expressPaymentMethod}>
                      <MonolithCartPaypalExpressContainer
                        selectorId="cart-braintree-paypal-checkout-button"
                        page="cart"
                        analyticsPageType={AnalyticsPageType.Cart}
                        shippingAddressEditable
                        shippingOptionsEditable
                        metricName="frontend.cart.error.count"
                        onError={handleExpressPayError}
                        rsCheckoutLocation={RSCheckoutLocation.Cart}
                      />
                    </Box>
                    <UserAgreement />
                    <Divider marginTop="m" marginBottom="m" />
                    <MonolithApplyCouponContainer />
                  </OrderSummary>
                </Box>
              </Box>
            </>
          ),
        })}
        <Box className={styles.signals}>
          <TrustSignalsGroup locale={locale} />
        </Box>
      </Box>
    </PageSection>
  );
};

export default CartPage;
